/**
 * Менеджер пагинации результатов поиска
 * Обрабатывает навигацию, обновление URL и динамическую загрузку
 */
class SearchPagination {
    constructor(config = {}) {
        this.config = {
            cardsContainer: '.results__cards',
            paginationContainer: '.results__pagination',
            placesContainer: '.results__places',
            loadMoreSelector: '.results__load-more',
            apiEndpoint: config.apiEndpoint,
            apiPopulars: config.apiPopulars,
            resultsPerPage: config.resultsPerPage || 10,
            maxPaginationLinks: config.maxPaginationLinks || 5,
            retryAttempts: config.retryAttempts || 3,
            retryDelay: config.retryDelay || 1000,
            skeletonDelay: config.skeletonDelay || 150,
            autoLoadOnMobile: config.autoLoadOnMobile ?? true,  // Автозагрузка при скролле (мобильные)
            autoLoadThreshold: config.autoLoadThreshold || 0.5, // 50% видимости кнопки
            mobileMaxWidth: config.mobileMaxWidth || 768,
            debug: config.debug ?? false,
            // SEO meta шаблоны
            siteName: config.siteName || 'ПоВятке',
            baseUrl: config.baseUrl || window.location.origin,
            titleHome: config.titleHome || '{siteName} - региональный поисковик',
            titleSearch: config.titleSearch || '{siteName} - региональный поиск: {query}{pageInfo}',
            descHome: config.descHome || 'Региональный поисковик {siteName}.',
            descSearch: config.descSearch || 'Региональный поисковик {siteName}. Результаты поиска {query}{descPageInfo}',
            ...config,
            messages: {
                noResultsTitle: 'По запросу "{query}" ничего не найдено.',
                noResultsHint: 'Попробуйте изменить запрос или использовать другие ключевые слова.',
                popularQueriesTitle: 'Популярные запросы сегодня:',
                errorTitle: 'Произошла ошибка при загрузке результатов.',
                errorHint: 'Пожалуйста, попробуйте позже.',
                loadMore: 'Загрузить ещё',
                loading: 'Идёт загрузка...',
                placesTitle: 'Места',
                ...(config.messages || {})
            }
        };

        this.elements = {
            cardsContainer: document.querySelector(this.config.cardsContainer),
            paginationContainer: document.querySelector(this.config.paginationContainer),
            placesContainer: document.querySelector(this.config.placesContainer),
            searchInput: document.querySelector('[data-searcher-input]'),
            searchButton: document.querySelector('[data-searcher-button]')
        };

        if (!this.elements.cardsContainer) {
            if (this.config.debug) {
                console.warn('[SearchPagination] cards container not found');
            }
            return;
        }

        this.currentPage = 1;
        this.totalPages = 1;
        this.currentQuery = '';
        this.isLoading = false;
        this.skeletonTimer = null;
        this.loadMoreObserver = null;  // IntersectionObserver для автозагрузки
        this.scrollTopButton = null;   // Кнопка "Вверх"
        this.isMobile = this.detectMobile();

        // Callback hooks (can be overridden)
        this.afterLoadResults = null;

        // Bound handlers for cleanup
        this.boundHandlers = {
            popstate: this.onPopState.bind(this),
            paginationClick: this.onPaginationClick.bind(this),
            searchSubmit: this.onSearchSubmit.bind(this),
            scroll: null  // Для кнопки "Вверх"
        };

        this.init();
    }

    /**
     * Initialize pagination
     */
    init() {
        // Get initial state from URL
        const urlParams = this.getUrlParams();
        this.currentQuery = urlParams.q || '';
        this.currentPage = parseInt(urlParams.page) || 1;

        // Set search input value from URL
        if (this.elements.searchInput) {
            this.elements.searchInput.value = this.currentQuery;
        }

        // Set initial history state
        if (!window.history.state) {
            window.history.replaceState(
                { query: this.currentQuery, page: this.currentPage },
                '',
                window.location.href
            );
        }

        // Setup event listeners
        window.addEventListener('popstate', this.boundHandlers.popstate);

        if (this.elements.paginationContainer) {
            this.elements.paginationContainer.addEventListener('click', this.boundHandlers.paginationClick);
        }

        // Обработчик click на searchButton в main.js

        // Setup "Load More" button for mobile
        const loadMoreBtn = document.querySelector('.results__more-button');
        if (loadMoreBtn) {
            this.boundHandlers.loadMore = this.onLoadMoreClick.bind(this);
            loadMoreBtn.addEventListener('click', this.boundHandlers.loadMore);
        }

        // Автозагрузка при скролле (мобильные)
        if (this.config.autoLoadOnMobile && this.isMobile) {
            this.initAutoLoad();
        }

        // Кнопка "Вверх" для мобильных
        if (this.isMobile) {
            this.createScrollTopButton();
        }
    }

    /**
     * Get URL parameters
     */
    getUrlParams() {
        const params = new URLSearchParams(window.location.search);
        return {
            q: params.get('q'),
            page: params.get('page')
        };
    }

    /**
     * Update URL without reload
     */
    updateUrl(query, page, pushState = true) {
        // Сохраняем предыдущий URL для referer
        const prevUrl = window.location.href;

        const params = new URLSearchParams();
        if (query) params.set('q', query);
        if (page > 1) params.set('page', page);

        const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;

        if (pushState) {
            window.history.pushState({ query, page }, '', newUrl);
        } else {
            window.history.replaceState({ query, page }, '', newUrl);
        }

        // Обновляем title и description
        this.updatePageMeta(query, page, this.totalPages);

        // Отправляем hit в Яндекс.Метрику (после обновления title)
        this.trackPageView(newUrl, prevUrl);
    }

    /**
     * Отправить просмотр страницы в Яндекс.Метрикe
     */
    trackPageView(url, referer) {
        if (typeof ym !== 'undefined') {
            const counterKey = Object.keys(window).find(k => k.startsWith('yaCounter'));
            if (counterKey) {
                const counterId = parseInt(counterKey.replace('yaCounter', ''));
                if (counterId) {
                    ym(counterId, 'hit', url, {
                        title: document.title,
                        referer: referer || ''
                    });
                }
            }
        }
    }

    /**
     * Обновить title и meta description страницы
     */
    updatePageMeta(query, page, totalPages) {
        const { siteName, titleHome, titleSearch, descHome, descSearch } = this.config;

        const baseTitle = titleHome.replace('{siteName}', siteName);
        const baseDescription = descHome.replace('{siteName}', siteName);

        if (!query) {
            // Главная страница без поиска
            document.title = baseTitle;
            this.setMetaDescription(baseDescription);
            this.updateH2('');
            return;
        }

        // Обрезаем запрос если слишком длинный (для title)
        const maxQueryLength = 50;
        const truncatedQuery = query.length > maxQueryLength
            ? query.substring(0, maxQueryLength) + '...'
            : query;

        // Формат title из шаблона
        const pageInfo = totalPages > 1 ? ` [${page}/${totalPages}]` : '';
        document.title = titleSearch
            .replace('{siteName}', siteName)
            .replace('{query}', truncatedQuery)
            .replace('{pageInfo}', pageInfo);

        // Meta description из шаблона
        const descPageInfo = totalPages > 1 ? ` страница ${page} из ${totalPages}` : '';
        const description = descSearch
            .replace('{siteName}', siteName)
            .replace('{query}', query)
            .replace('{descPageInfo}', descPageInfo);
        this.setMetaDescription(description);
        this.updateH2(query, page);
    }

    /**
     * Обновить или создать h2 с результатами поиска
     */
    updateH2(query, page = 1) {
        let h2 = document.querySelector('h2.sr-only');

        if (!query) {
            // Удаляем h2 если нет запроса
            if (h2) h2.remove();
            return;
        }

        const pageInfo = page > 1 ? ` [страница ${page}]` : '';
        const text = `Результаты поиска: ${query}${pageInfo}`;

        if (h2) {
            h2.textContent = text;
        } else {
            // Создаём h2 после h1
            const h1 = document.querySelector('h1.sr-only');
            if (h1) {
                h2 = document.createElement('h2');
                h2.className = 'sr-only';
                h2.textContent = text;
                h1.after(h2);
            }
        }
    }

    /**
     * Установить meta description
     */
    setMetaDescription(content) {
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
            meta = document.createElement('meta');
            meta.name = 'description';
            document.head.appendChild(meta);
        }
        meta.content = content;
    }

    /**
     * Handle browser back/forward buttons
     */
    onPopState(event) {
        if (event.state) {
            this.currentQuery = event.state.query || '';
            this.currentPage = event.state.page || 1;

            // Update search input value (bug fix: back button)
            if (this.elements.searchInput) {
                this.elements.searchInput.value = this.currentQuery;

                // Update clear button state
                const clearButton = document.querySelector('[data-searcher-clear]');
                if (clearButton) {
                    if (this.currentQuery) {
                        clearButton.classList.add('_active');
                    } else {
                        clearButton.classList.remove('_active');
                    }
                }
            }

            if (this.currentQuery) {
                this.loadResults(this.currentQuery, this.currentPage, false);
                // Show results mode
                if (window.showSearchResults) {
                    window.showSearchResults();
                }
            } else {
                // Clear results when going back to empty search
                if (this.elements.cardsContainer) {
                    this.elements.cardsContainer.innerHTML = '';
                }
                if (this.elements.paginationContainer) {
                    this.elements.paginationContainer.innerHTML = '';
                }
                this.hideLoadMoreButton();

                // Сбрасываем title и description
                this.updatePageMeta('', 1, 1);

                // Switch to search mode
                if (window.showSearchMode) {
                    window.showSearchMode();
                }

                // Remove button states
                if (this.elements.searchButton) {
                    this.elements.searchButton.classList.remove('_searched');
                }
            }
        }
    }

    /**
     * Handle pagination link clicks
     */
    onPaginationClick(e) {
        const target = e.target.closest('a.pagination__page-link, a.pagination__page-next, a.pagination__page-prev');
        if (!target) return;

        e.preventDefault();

        const href = target.getAttribute('href');
        const params = new URLSearchParams(href.split('?')[1] || '');
        const page = parseInt(params.get('page')) || 1;

        this.goToPage(page);
    }

    /**
     * Handle search button click
     */
    onSearchSubmit(e) {
        e.preventDefault();

        if (!this.elements.searchInput) return;

        const query = this.elements.searchInput.value.trim();
        if (!query) return;

        // Переключаем в режим результатов
        if (typeof window.showSearchResults === 'function') {
            window.showSearchResults();
        }

        this.currentQuery = query;
        this.currentPage = 1;
        this.loadResults(query, 1, true);
    }

    /**
     * Handle "Load More" button click
     */
    onLoadMoreClick(e) {
        e.preventDefault();

        if (this.currentPage >= this.totalPages) return;
        if (this.isLoading) return;

        this.currentPage++;
        this.appendResults(this.currentQuery, this.currentPage);
    }

    /**
     * Append results instead of replacing (for mobile)
     */
    async appendResults(query, page) {
        if (this.isLoading) return;
        if (!query) return;

        this.isLoading = true;
        const loadMoreBtn = document.querySelector('.results__more-button, .results__load-more button');
        const loadMoreContainer = document.querySelector('.results__load-more');

        if (loadMoreBtn) {
            loadMoreBtn.classList.add('_loading');
            loadMoreBtn.textContent = this.config.messages.loading;
        }
        if (loadMoreContainer) {
            loadMoreContainer.classList.add('_loading');
        }

        try {
            const params = new URLSearchParams({
                q: query,
                page: page,
                limit: this.config.resultsPerPage
            });

            const response = await this.fetchWithRetry(`${this.config.apiEndpoint}?${params.toString()}`);
            const data = await response.json();

            if (data.results && Array.isArray(data.results) && data.results.length > 0) {
                const startIndex = (page - 1) * this.config.resultsPerPage;

                const newCards = data.results.map((result, index) => {
                    const num = startIndex + index + 1;
                    const faviconClass = this.getFaviconClass(num);
                    const domain = result.domain || this.extractDomain(result.url);
                    return `
                    <div class="results__card" data-uid="${this.escapeHtml(result.id || '')}">
                        <div class="${faviconClass}">
                            ${num}.
                        </div>
                        <h3 class="results__card-title">
                            <a href="${this.escapeHtml(result.url)}" target="_blank" rel="noopener">${this.highlightQuery(result.title, query)}</a>
                        </h3>
                        <div class="results__card-domain">
                            <img class="results__card-domain-favicon" src="https://${this.escapeHtml(domain)}/favicon.ico" width="18" height="18" alt="" loading="lazy" onerror="this.style.display='none'">${this.escapeHtml(domain)}
                        </div>
                        <div class="results__card-snippet">
                            ${this.highlightQuery(result.snippet, query)}
                        </div>
                        <span class="icon_menu results__card-menu-button" data-card-menu-trigger="${result.id}"></span>
                    </div>
                `}).join('');

                // Append to existing cards
                if (this.elements.cardsContainer) {
                    this.elements.cardsContainer.insertAdjacentHTML('beforeend', newCards);
                }

                this.currentPage = page;
                this.totalPages = data.totalPages || 1;
                this.updateUrl(query, page, true);

                // Update SearchLinker cards data
                if (window.searchLinkerHandle) {
                    window.searchLinkerHandle.updateCardsList();
                }

                // Update "Load More" button visibility
                this.updateLoadMoreButton(page, this.totalPages);

                // Показываем кнопку "Вверх" после подгрузки (мобильные)
                if (this.isMobile && page > 1) {
                    this.showScrollTopButton();
                }
            }

        } catch (err) {
            this.error('Ошибка добавления результатов:', err);
        } finally {
            this.isLoading = false;
            if (loadMoreBtn) {
                loadMoreBtn.classList.remove('_loading');
                loadMoreBtn.textContent = this.config.messages.loadMore;
            }
            if (loadMoreContainer) {
                loadMoreContainer.classList.remove('_loading');
            }
        }
    }

    /**
     * Load more results (wrapper for appendResults)
     */
    async loadMoreResults(query, page) {
        return this.appendResults(query, page);
    }

    /**
     * Navigate to specific page
     */
    goToPage(page) {
        if (page < 1 || page > this.totalPages || page === this.currentPage) return;
        if (this.isLoading) return;

        this.currentPage = page;
        this.loadResults(this.currentQuery, page, true);

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    /**
     * Load search results from API
     */
    async loadResults(query, page = 1, pushState = true) {
        if (this.isLoading) return;
        if (!query) return;

        this.isLoading = true;
        this.currentQuery = query;
        this.currentPage = page;
        this.showLoading();

        // Скрываем кнопку "Вверх" при новом поиске
        if (page === 1) {
            this.hideScrollTopButton();
            this.hidePlaces();
        }

        try {
            const params = new URLSearchParams({
                q: query,
                page: page,
                limit: this.config.resultsPerPage
            });

            const response = await this.fetchWithRetry(`${this.config.apiEndpoint}?${params.toString()}`);
            const data = await response.json();

            if (data.results && Array.isArray(data.results) && data.results.length > 0) {
                // Рендерим places только на первой странице
                if (page === 1 && data.places && data.places.length > 0) {
                    this.renderPlaces(data.places);
                } else {
                    this.hidePlaces();
                }

                this.renderResults(data.results);
                this.totalPages = data.totalPages || 1;
                this.renderPagination(page, this.totalPages);
                this.renderFooter();

                // Обновляем URL и мета-теги после получения totalPages
                this.updateUrl(query, page, pushState);

                // Update "Load More" button visibility
                this.updateLoadMoreButton(page, this.totalPages);

                // Update SearchLinker cards data
                if (window.searchLinkerHandle) {
                    window.searchLinkerHandle.updateCardsList();
                }

                // Снимаем фокус с input (закрывает клавиатуру на мобильных)
                if (this.elements.searchInput) {
                    this.elements.searchInput.blur();
                }

                // Call callback hook
                if (typeof this.afterLoadResults === 'function') {
                    this.afterLoadResults(true, data);
                }
            } else {
                this.showNoResults();
                this.totalPages = 0;

                // Обновляем URL и мета-теги
                this.updateUrl(query, page, pushState);

                // Hide "Load More" button when no results
                this.hideLoadMoreButton();

                // Call callback hook
                if (typeof this.afterLoadResults === 'function') {
                    this.afterLoadResults(false, data);
                }
            }

        } catch (err) {
            this.error('Ошибка загрузки результатов поиска:', err);
            this.showError();
            this.hideLoadMoreButton();
        } finally {
            this.isLoading = false;
            this.hideLoading();

            // Remove loading state from searcher
            if (window.searcherHandle && typeof window.searcherHandle.removeLoadingState === 'function') {
                window.searcherHandle.removeLoadingState();
            }
        }
    }

    /**
     * Render search result cards
     */
    renderResults(results) {
        if (!this.elements.cardsContainer) return;

        const startIndex = (this.currentPage - 1) * this.config.resultsPerPage;

        this.elements.cardsContainer.innerHTML = results.map((result, index) => {
            const num = startIndex + index + 1;
            const faviconClass = this.getFaviconClass(num);
            const domain = result.domain || this.extractDomain(result.url);
            return `
            <div class="results__card" data-uid="${this.escapeHtml(result.id || '')}">
                <div class="${faviconClass}">
                    ${num}.
                </div>
                <h3 class="results__card-title">
                    <a href="${this.escapeHtml(result.url)}" target="_blank" rel="noopener">${this.highlightQuery(result.title, this.currentQuery)}</a>
                </h3>
                <div class="results__card-domain">
                    <img class="results__card-domain-favicon" src="https://${this.escapeHtml(domain)}/favicon.ico" width="18" height="18" alt="" loading="lazy" onerror="this.style.display='none'">${this.escapeHtml(domain)}
                </div>
                <div class="results__card-snippet">
                    ${this.highlightQuery(result.snippet, this.currentQuery)}
                </div>
                <span class="icon_menu results__card-menu-button" data-card-menu-trigger="${result.id}"></span>
            </div>
        `}).join('');
    }

    /**
     * Render places block (компании)
     */
    renderPlaces(places) {
        if (!this.elements.placesContainer) return;
        if (!places || places.length === 0) {
            this.hidePlaces();
            return;
        }

        this.elements.placesContainer.classList.remove('_hidden');
        this.elements.placesContainer.innerHTML = `
            <div class="places">
                <div class="places__header">
                    <h2 class="places__title">${this.config.messages.placesTitle}</h2>
                </div>
                <div class="places__scroll-wrapper">
                    <button class="places__scroll-btn places__scroll-btn_prev" aria-label="Назад" disabled>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <div class="places__list">
                        ${places.map(place => this.renderPlaceCard(place)).join('')}
                    </div>
                    <button class="places__scroll-btn places__scroll-btn_next" aria-label="Вперёд">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
        `;

        this.attachPlacesEventListeners();
    }

    /**
     * Render single place card
     */
    renderPlaceCard(place) {
        const hasImages = place.images && place.images.length > 0;
        const isOpen = this.isPlaceOpenNow(place.workingHours);
        const todayHours = this.getHoursLabel(place.workingHours, isOpen);

        return `
            <article class="places__card" data-place-id="${this.escapeHtml(place.id)}">
                <a href="${this.escapeHtml(place.website)}" class="places__card-link" target="_blank" rel="noopener">
                    <div class="places__card-gallery${hasImages ? '' : ' places__card-gallery_empty'}">
                        ${hasImages ? this.renderPlaceGallery(place.images) : this.renderPlaceEmptyGallery()}
                        ${place.logo ? `
                            <div class="places__card-logo">
                                <img src="${this.escapeHtml(place.logo)}" alt="" loading="lazy" onload="this.parentElement.classList.add('_loaded')" onerror="this.parentElement.classList.add('_hidden')">
                            </div>
                        ` : ''}
                    </div>
                    <div class="places__card-content">
                        <h3 class="places__card-name"><span>${this.escapeHtml(place.name)}</span>${place.verified ? '<svg class="places__card-verified" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Подтверждено"><path d="M12 2l2.4 1.6 2.9-.35 1.05 2.73 2.52 1.5-.35 2.9L22 12l-1.49 1.62.35 2.9-2.52 1.5-1.05 2.73-2.9-.35L12 22l-2.4-1.6-2.9.35-1.05-2.73-2.52-1.5.35-2.9L2 12l1.49-1.62-.35-2.9 2.52-1.5 1.05-2.73 2.9.35z" fill="#1877F2"/><path d="M8 12l2.5 2.5 5.5-5.5" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}</h3>
                        <div class="places__card-meta">
                            ${place.rating ? `
                                <span class="places__card-rating">
                                    <span class="places__card-rating-value ${parseFloat(place.rating) < 2 ? 'places__card-rating-value_red' : parseFloat(place.rating) < 4 ? 'places__card-rating-value_yellow' : ''}">${place.rating}</span>
                                    ${place.reviewsCount ? `<span class="places__card-reviews">${this.formatReviews(place.reviewsCount)}</span>` : ''}
                                </span>
                                <span class="places__card-sep">·</span>
                            ` : ''}
                            ${place.category ? `<span class="places__card-category">${this.escapeHtml(place.category)}</span>` : ''}
                        </div>
                        <div class="places__card-hours places__card-hours_${isOpen ? 'open' : 'closed'}">
                            ${isOpen ? 'Открыто' : 'Закрыто'}${todayHours ? ` · ${todayHours}` : ''}
                        </div>
                        <div class="places__card-address">${this.escapeHtml(place.address)}</div>
                    </div>
                </a>
                ${place.phone ? `
                    <button class="places__card-phone" data-phone="${this.escapeHtml(place.phone)}">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Показать телефон
                    </button>
                ` : ''}
            </article>
        `;
    }

    /**
     * Render place gallery with indicators
     */
    renderPlaceGallery(images) {
        const maxImages = Math.min(images.length, 5);
        const visibleImages = images.slice(0, maxImages);

        return `
            <div class="places__card-images" data-images-count="${visibleImages.length}">
                ${visibleImages.map((img, i) => `
                    <img src="${this.escapeHtml(img)}" 
                         alt="" 
                         class="places__card-image${i === 0 ? ' _active' : ''}" 
                         data-index="${i}"
                         ${i === 0 ? '' : 'loading="lazy" '}onerror="this.classList.add('_error')">
                `).join('')}
            </div>
            ${visibleImages.length > 1 ? `
                <div class="places__card-indicators">
                    ${visibleImages.map((_, i) => `
                        <span class="places__card-indicator${i === 0 ? ' _active' : ''}" data-index="${i}"></span>
                    `).join('')}
                </div>
            ` : ''}
        `;
    }

    /**
     * Render empty gallery placeholder
     */
    renderPlaceEmptyGallery() {
        return `
            <div class="places__card-images places__card-images_empty">
                <div class="places__card-no-photo">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/>
                        <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                        <path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
            </div>
        `;
    }

    /**
     * Hide places block
     */
    hidePlaces() {
        if (this.elements.placesContainer) {
            this.elements.placesContainer.innerHTML = '';
            this.elements.placesContainer.classList.add('_hidden');
        }
    }

    /**
     * Attach event listeners for places
     */
    attachPlacesEventListeners() {
        if (!this.elements.placesContainer) return;

        const list = this.elements.placesContainer.querySelector('.places__list');
        const prevBtn = this.elements.placesContainer.querySelector('.places__scroll-btn_prev');
        const nextBtn = this.elements.placesContainer.querySelector('.places__scroll-btn_next');

        // Scroll buttons
        if (prevBtn && nextBtn && list) {
            prevBtn.addEventListener('click', () => this.scrollPlaces(list, -1));
            nextBtn.addEventListener('click', () => this.scrollPlaces(list, 1));

            list.addEventListener('scroll', () => this.updatePlacesScrollButtons(list, prevBtn, nextBtn));
            this.updatePlacesScrollButtons(list, prevBtn, nextBtn);
        }

        // Hover slideshow for images
        const galleries = this.elements.placesContainer.querySelectorAll('.places__card-images[data-images-count]');
        galleries.forEach(gallery => {
            const count = parseInt(gallery.dataset.imagesCount);
            if (count > 1) {
                gallery.addEventListener('mousemove', (e) => this.onPlaceGalleryHover(e, gallery, count));
                gallery.addEventListener('mouseleave', () => this.onPlaceGalleryLeave(gallery));
            }
        });

        // Phone reveal
        const phoneButtons = this.elements.placesContainer.querySelectorAll('.places__card-phone');
        phoneButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const phone = btn.dataset.phone;
                btn.innerHTML = `<a href="tel:${phone}">${phone}</a>`;
                btn.classList.add('_revealed');
            });
        });
    }

    /**
     * Gallery hover - switch images
     */
    onPlaceGalleryHover(e, gallery, count) {
        const rect = gallery.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const width = rect.width;
        const index = Math.floor((x / width) * count);

        this.showPlaceImage(gallery, Math.min(index, count - 1));
    }

    /**
     * Gallery mouse leave - return to first image
     */
    onPlaceGalleryLeave(gallery) {
        this.showPlaceImage(gallery, 0);
    }

    /**
     * Show specific image in gallery
     */
    showPlaceImage(gallery, index) {
        const images = gallery.querySelectorAll('.places__card-image');
        const card = gallery.closest('.places__card');
        const indicators = card ? card.querySelectorAll('.places__card-indicator') : [];

        images.forEach((img, i) => {
            img.classList.toggle('_active', i === index);
        });

        indicators.forEach((ind, i) => {
            ind.classList.toggle('_active', i === index);
        });
    }

    /**
     * Scroll places list
     */
    scrollPlaces(list, direction) {
        const cardWidth = list.querySelector('.places__card')?.offsetWidth || 220;
        const gap = 16;
        const scrollAmount = (cardWidth + gap) * 2;

        list.scrollBy({
            left: direction * scrollAmount,
            behavior: 'smooth'
        });
    }

    /**
     * Update scroll buttons state
     */
    updatePlacesScrollButtons(list, prevBtn, nextBtn) {
        const isAtStart = list.scrollLeft <= 0;
        const isAtEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;

        prevBtn.disabled = isAtStart;
        nextBtn.disabled = isAtEnd;
    }

    /**
     * Parse workingHours string
     */
    parsePlaceWorkingHours(str) {
        if (!str) return { weekly: [], exceptions: {} };

        const parts = str.split(';');
        const weekly = parts.slice(0, 7);
        const exceptions = {};

        parts.slice(7).forEach(part => {
            const [date, time] = part.split('=');
            if (date && time) {
                exceptions[date] = time;
            }
        });

        return { weekly, exceptions };
    }

    /**
     * Get today's hours for place
     */
    getPlaceTodayHours(workingHours) {
        const { weekly, exceptions } = this.parsePlaceWorkingHours(workingHours);
        const now = new Date();

        // Check exception for today
        const today = now.toISOString().slice(0, 10);
        if (exceptions[today]) {
            return exceptions[today];
        }

        // Get from weekly schedule (0=Mon, 6=Sun)
        const dayIndex = (now.getDay() + 6) % 7;
        return weekly[dayIndex] || null;
    }

    /**
     * Get formatted today hours
     */
    getPlaceTodayHoursFormatted(workingHours) {
        return this.getHoursLabel(workingHours, this.isPlaceOpenNow(workingHours));
    }

    /**
     * Get hours label depending on open/closed state.
     * Open:   "до HH:MM" — closing time of current interval
     * Closed: "до HH:MM" — next opening time (today or next days)
     */
    getHoursLabel(workingHours, isOpen) {
        const now = new Date();
        const currentTime = now.toTimeString().slice(0, 5);

        if (isOpen) {
        const hours = this.getPlaceTodayHours(workingHours);
        if (hours === '24h') return 'круглосуточно';
        const intervals = hours.split(',');
            for (const interval of intervals) {
                const [open, close] = interval.split('-');
                if (currentTime >= open && currentTime < close) {
                    return `до ${close}`;
                }
            }
            return '';
        }

        // Closed: check only the next calendar day
        const { weekly, exceptions } = this.parsePlaceWorkingHours(workingHours);

        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowDate = tomorrow.toISOString().slice(0, 10);
        const tomorrowIndex = (tomorrow.getDay() + 6) % 7; // 0=Mon…6=Sun

        const tomorrowHours = exceptions[tomorrowDate] !== undefined
            ? exceptions[tomorrowDate]
            : (weekly[tomorrowIndex] || null);

        if (!tomorrowHours || tomorrowHours === 'closed') return '';
        if (tomorrowHours === '24h') return 'до 00:00';

        const [firstOpen] = tomorrowHours.split(',')[0].split('-');
        return firstOpen ? `до ${firstOpen}` : '';
    }

    /**
     * Check if place is open now
     */
    isPlaceOpenNow(workingHours) {
        const hours = this.getPlaceTodayHours(workingHours);

        if (!hours || hours === 'closed') return false;
        if (hours === '24h') return true;

        const now = new Date();
        const currentTime = now.toTimeString().slice(0, 5);

        // Support breaks (multiple intervals)
        const intervals = hours.split(',');
        return intervals.some(interval => {
            const [open, close] = interval.split('-');
            return currentTime >= open && currentTime < close;
        });
    }

    /**
     * Format reviews count
     */
    formatReviews(count) {
        if (count >= 1000) {
            return `${Math.floor(count / 1000)}K отзывов`;
        }

        const lastDigit = count % 10;
        const lastTwoDigits = count % 100;

        let word = 'отзывов';
        if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
            word = 'отзывов';
        } else if (lastDigit === 1) {
            word = 'отзыв';
        } else if (lastDigit >= 2 && lastDigit <= 4) {
            word = 'отзыва';
        }

        return `${count} ${word}`;
    }

    /**
     * Render pagination
     */
    renderPagination(currentPage, totalPages) {
        // Обновляем link rel="prev/next/canonical" в head
        this.updateHeadLinks(currentPage, totalPages);

        if (!this.elements.paginationContainer || totalPages <= 1) {
            if (this.elements.paginationContainer) {
                this.elements.paginationContainer.innerHTML = '';
            }
            return;
        }

        const maxLinks = this.config.maxPaginationLinks;
        let startPage = Math.max(1, currentPage - Math.floor(maxLinks / 2));
        let endPage = Math.min(totalPages, startPage + maxLinks - 1);

        // Adjust start if we're near the end
        if (endPage - startPage < maxLinks - 1) {
            startPage = Math.max(1, endPage - maxLinks + 1);
        }

        let paginationHtml = '<nav class="pagination" role="navigation" aria-label="Pagination">';
        paginationHtml += '<ul class="pagination-list" style="list-style:none;display:flex;gap:8px;padding:0;margin:0;align-items:center;">';

        // Previous button
        if (currentPage > 1) {
            paginationHtml += `
                <li>
                    <a class="pagination__page-prev" href="${this.buildPageUrl(currentPage - 1)}" rel="prev" aria-label="Предыдущая">
                        Предыдущая
                    </a>
                </li>
            `;
        }

        // First page
        if (startPage > 1) {
            paginationHtml += `
                <li>
                    <a class="pagination__page-link" href="${this.buildPageUrl(1)}" aria-label="На страницу 1">1</a>
                </li>
            `;
            if (startPage > 2) {
                paginationHtml += '<li><span class="pagination__ellipsis">...</span></li>';
            }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            if (i === currentPage) {
                paginationHtml += `
                    <li>
                        <span class="pagination__page-current" aria-current="page">${i}</span>
                    </li>
                `;
            } else {
                paginationHtml += `
                    <li>
                        <a class="pagination__page-link" href="${this.buildPageUrl(i)}" aria-label="На страницу ${i}">${i}</a>
                    </li>
                `;
            }
        }

        // Многоточие в конце (без ссылки на последнюю страницу)
        if (endPage < totalPages) {
            paginationHtml += '<li><span class="pagination__ellipsis">...</span></li>';
        }

        // Next button
        if (currentPage < totalPages) {
            paginationHtml += `
                <li>
                    <a class="pagination__page-next" href="${this.buildPageUrl(currentPage + 1)}" rel="next" aria-label="Следующая">
                        Следующая
                    </a>
                </li>
            `;
        }

        paginationHtml += '</ul></nav>';

        this.elements.paginationContainer.innerHTML = paginationHtml;
    }

    /**
     * Построить URL для страницы пагинации
     */
    buildPageUrl(page) {
        const params = new URLSearchParams();
        params.set('q', this.currentQuery);
        if (page > 1) {
            params.set('page', page);
        }
        return `${this.config.baseUrl}/?${params.toString()}`;
    }

    /**
     * Обновить link rel="prev/next/canonical" в head
     */
    updateHeadLinks(currentPage, totalPages) {
        const baseUrl = this.config.baseUrl;

        // Удаляем старые link теги
        document.querySelectorAll('link[rel="prev"], link[rel="next"], link[rel="canonical"]').forEach(el => el.remove());

        // canonical — всегда указывает на текущую страницу
        const canonical = document.createElement('link');
        canonical.rel = 'canonical';
        canonical.href = this.currentQuery ? this.buildPageUrl(currentPage) : baseUrl + '/';
        document.head.appendChild(canonical);

        // prev — если не первая страница
        if (currentPage > 1) {
            const prev = document.createElement('link');
            prev.rel = 'prev';
            prev.href = this.buildPageUrl(currentPage - 1);
            document.head.appendChild(prev);
        }

        // next — если не последняя страница
        if (currentPage < totalPages) {
            const next = document.createElement('link');
            next.rel = 'next';
            next.href = this.buildPageUrl(currentPage + 1);
            document.head.appendChild(next);
        }
    }

    /**
     * Render footer with test mode notice
     */
    renderFooter(centered = false) {
        // Удаляем старый футер если есть
        const existingFooter = document.querySelector('.results__footer');
        if (existingFooter) {
            existingFooter.remove();
        }

        const footer = document.createElement('div');
        footer.className = 'results__footer' + (centered ? ' results__footer_centered' : '');
        footer.innerHTML = `
            Региональный поисковик работает в тестовом режиме. 
            <a href="https://t.me/teltsn" target="_blank" rel="noopener nofollow">Связаться с нами</a>
        `;

        // Вставляем после пагинации или после карточек
        if (this.elements.paginationContainer && this.elements.paginationContainer.innerHTML) {
            this.elements.paginationContainer.after(footer);
        } else if (this.elements.cardsContainer) {
            this.elements.cardsContainer.after(footer);
        }
    }

    /**
     * Highlight search query in text
     */
    highlightQuery(text, query) {
        if (!text || !query) return this.escapeHtml(text || '');

        const escapedText = this.escapeHtml(text);
        const words = query.split(/\s+/).filter(w => w.length > 0);

        let result = escapedText;
        words.forEach(word => {
            const regex = new RegExp(`(${this.escapeRegex(word)})`, 'gi');
            result = result.replace(regex, '<b>$1</b>');
        });

        return result;
    }

    /**
     * Extract domain from URL
     */
    extractDomain(url) {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname.replace(/^www\./, '');
        } catch {
            return url;
        }
    }

    /**
     * Получить класс для favicon в зависимости от числа
     */
    getFaviconClass(num) {
        let cls = 'results__card-favicon';
        if (num >= 1000) {
            cls += ' results__card-favicon_4col';
        } else if (num >= 100) {
            cls += ' results__card-favicon_3col';
        }
        return cls;
    }

    /**
     * Показать состояние загрузки со скелетонами (с задержкой)
     */
    showLoading() {
        // Скрываем кнопку "Загрузить ещё" сразу
        this.hideLoadMoreButton();

        // Показываем скелетоны с задержкой, чтобы не мигать при быстром ответе
        this.skeletonTimer = setTimeout(() => {
            if (this.elements.cardsContainer && this.isLoading) {
                this.elements.cardsContainer.classList.add('results__cards_loading');

                const skeletonCount = this.config.resultsPerPage || 10;
                const skeletons = Array(skeletonCount).fill(0).map(() => `
                    <div class="results__skeleton">
                        <div class="results__skeleton-favicon"></div>
                        <div class="results__skeleton-title"></div>
                        <div class="results__skeleton-domain"></div>
                        <div class="results__skeleton-snippet"></div>
                    </div>
                `).join('');

                this.elements.cardsContainer.innerHTML = skeletons;
            }
        }, this.config.skeletonDelay);
    }

    /**
     * Скрыть состояние загрузки
     */
    hideLoading() {
        // Отменяем таймер скелетонов если ответ пришёл раньше
        if (this.skeletonTimer) {
            clearTimeout(this.skeletonTimer);
            this.skeletonTimer = null;
        }

        if (this.elements.cardsContainer) {
            this.elements.cardsContainer.classList.remove('results__cards_loading');
        }
    }

    /**
     * Показать сообщение "ничего не найдено" с популярными запросами
     */
    showNoResults() {
        if (this.elements.cardsContainer) {
            const title = this.config.messages.noResultsTitle.replace('{query}', this.escapeHtml(this.currentQuery));

            this.elements.cardsContainer.innerHTML = `
                <div class="results__empty">
                    <p>${title}</p>
                    <p>${this.config.messages.noResultsHint}</p>
                </div>
                <div class="results__populars">
                    <p class="results__populars-title">${this.config.messages.popularQueriesTitle}</p>
                    <ul class="results__populars-list"></ul>
                </div>
            `;

            // Загружаем популярные запросы
            this.loadPopulars();
        }
        if (this.elements.paginationContainer) {
            this.elements.paginationContainer.innerHTML = '';
        }
        this.hidePlaces();
        this.hideLoadMoreButton();
        this.renderFooter(true);
    }

    /**
     * Загрузка и рендер популярных запросов
     */
    async loadPopulars() {
        try {
            const response = await this.fetchWithRetry(this.config.apiPopulars);
            const data = await response.json();

            if (Array.isArray(data) && data.length > 0) {
                this.renderPopulars(data.slice(0, 10));
            }
        } catch (err) {
            this.error('Ошибка загрузки популярных запросов:', err);
            // Скрываем блок при ошибке
            const popularsSection = this.elements.cardsContainer?.querySelector('.results__populars');
            if (popularsSection) {
                popularsSection.classList.add('_hidden');
            }
        }
    }

    /**
     * Render popular queries list
     */
    renderPopulars(tags) {
        const listElement = this.elements.cardsContainer?.querySelector('.results__populars-list');
        if (!listElement) return;

        listElement.innerHTML = tags
            .map(tag => `<li>${this.escapeHtml(tag)}</li>`)
            .join('');

        // Handle tag click
        listElement.querySelectorAll('li').forEach(li => {
            li.addEventListener('click', () => {
                const query = li.textContent || li.innerText;
                if (this.elements.searchInput) {
                    this.elements.searchInput.value = query;

                    // Пересчитать высоту textarea
                    if (window.searcherHandle?.resizeAndToggleClear) {
                        window.searcherHandle.resizeAndToggleClear();
                    }

                    // Update clear button
                    const clearButton = document.querySelector('[data-searcher-clear]');
                    if (clearButton) {
                        clearButton.classList.add('_active');
                    }
                }
                // Trigger search
                this.currentQuery = query;
                this.currentPage = 1;
                this.loadResults(query, 1, true);
            });
        });
    }

    /**
     * Обновить видимость кнопки "Загрузить ещё"
     */
    updateLoadMoreButton(currentPage, totalPages) {
        const loadMoreBtn = document.querySelector('.results__more-button, .results__load-more button, [data-load-more-button]');
        const loadMoreContainer = document.querySelector('.results__load-more');

        if (loadMoreBtn || loadMoreContainer) {
            const element = loadMoreContainer || loadMoreBtn;
            if (currentPage >= totalPages) {
                element.classList.add('_hidden');
            } else {
                element.classList.remove('_hidden');
            }
        }
    }

    /**
     * Скрыть кнопку "Загрузить ещё"
     */
    hideLoadMoreButton() {
        const loadMoreBtn = document.querySelector('.results__more-button, .results__load-more button, [data-load-more-button]');
        const loadMoreContainer = document.querySelector('.results__load-more');

        if (loadMoreContainer) {
            loadMoreContainer.classList.add('_hidden');
        } else if (loadMoreBtn) {
            loadMoreBtn.classList.add('_hidden');
        }
    }

    /**
     * Определение мобильного устройства
     */
    detectMobile() {
        return /Mobi|Android|iPhone/i.test(navigator.userAgent)
            || window.innerWidth < this.config.mobileMaxWidth;
    }

    /**
     * Инициализация автозагрузки при скролле
     */
    initAutoLoad() {
        if (!('IntersectionObserver' in window)) {
            this.warn('IntersectionObserver не поддерживается, автозагрузка отключена');
            return;
        }

        this.loadMoreObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !this.isLoading) {
                        this.onAutoLoad();
                    }
                });
            },
            { threshold: this.config.autoLoadThreshold }
        );

        // Наблюдаем за контейнером кнопки
        const loadMoreContainer = document.querySelector(this.config.loadMoreSelector);
        if (loadMoreContainer) {
            this.loadMoreObserver.observe(loadMoreContainer);
            this.log('Автозагрузка включена');
        }
    }

    /**
     * Обработчик автозагрузки
     */
    onAutoLoad() {
        // Проверяем есть ли ещё страницы
        if (this.currentPage >= this.totalPages) {
            return;
        }

        // Проверяем есть ли запрос
        if (!this.currentQuery) {
            return;
        }

        this.log('Автозагрузка страницы', this.currentPage + 1);

        // Загружаем следующую страницу
        this.appendResults(this.currentQuery, this.currentPage + 1);
    }

    /* ==================== КНОПКА "ВВЕРХ" ==================== */

    /**
     * Создание кнопки "Вверх"
     */
    createScrollTopButton() {
        // Проверяем, не создана ли уже
        if (document.querySelector('.results__2top-button')) return;

        const button = document.createElement('button');
        button.className = 'results__2top-button';
        button.setAttribute('aria-label', 'Вернуться наверх');
        button.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 15l-6-6-6 6"/>
            </svg>
        `;

        button.addEventListener('click', () => this.scrollToTop());

        document.body.appendChild(button);
        this.scrollTopButton = button;

        // Слушатель скролла для показа/скрытия
        this.boundHandlers.scroll = this.onScrollForTopButton.bind(this);
        window.addEventListener('scroll', this.boundHandlers.scroll, { passive: true });

        this.log('Кнопка "Вверх" создана');
    }

    /**
     * Показать кнопку "Вверх"
     */
    showScrollTopButton() {
        if (this.scrollTopButton) {
            this.scrollTopButton.classList.add('_visible');
        }
    }

    /**
     * Скрыть кнопку "Вверх"
     */
    hideScrollTopButton() {
        if (this.scrollTopButton) {
            this.scrollTopButton.classList.remove('_visible');
        }
    }

    /**
     * Обработчик скролла для кнопки "Вверх"
     */
    onScrollForTopButton() {
        // Скрываем если близко к верху (меньше 500px от верха)
        if (window.scrollY < 500) {
            this.hideScrollTopButton();
        } else if (this.currentPage > 1) {
            // Показываем только если была подгрузка
            this.showScrollTopButton();
        }
    }

    /**
     * Плавная прокрутка вверх
     */
    scrollToTop() {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    /**
     * Показать сообщение об ошибке
     */
    showError() {
        if (this.elements.cardsContainer) {
            this.elements.cardsContainer.innerHTML = `
                <div class="results__error">
                    <p>${this.config.messages.errorTitle}</p>
                    <p>${this.config.messages.errorHint}</p>
                </div>
            `;
        }
        if (this.elements.paginationContainer) {
            this.elements.paginationContainer.innerHTML = '';
        }
        this.hidePlaces();
        this.hideLoadMoreButton();
        this.renderFooter(true);
    }

    /**
     * Escape HTML
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Экранирование спецсимволов regex
     */
    escapeRegex(text) {
        return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /**
     * Логирование (только если debug: true)
     */
    log(...args) {
        if (this.config.debug) {
            console.log('[SearchPagination]', ...args);
        }
    }

    warn(...args) {
        if (this.config.debug) {
            console.warn('[SearchPagination]', ...args);
        }
    }

    error(...args) {
        if (this.config.debug) {
            console.error('[SearchPagination]', ...args);
        }
    }

    /**
     * Fetch с повторными попытками при ошибке
     */
    async fetchWithRetry(url, options = {}) {
        let lastError;

        for (let attempt = 1; attempt <= this.config.retryAttempts; attempt++) {
            try {
                const response = await fetch(url, options);

                if (!response.ok) {
                    throw new Error(`HTTP ошибка: ${response.status}`);
                }

                return response;
            } catch (err) {
                lastError = err;
                this.warn(`Попытка ${attempt}/${this.config.retryAttempts} не удалась:`, err.message);

                // Не ждём после последней попытки
                if (attempt < this.config.retryAttempts) {
                    await new Promise(resolve => setTimeout(resolve, this.config.retryDelay));
                }
            }
        }

        throw lastError;
    }

    /**
     * Очистка
     */
    destroy() {
        // Отменяем таймер скелетонов
        if (this.skeletonTimer) {
            clearTimeout(this.skeletonTimer);
            this.skeletonTimer = null;
        }

        // Отключаем IntersectionObserver автозагрузки
        if (this.loadMoreObserver) {
            this.loadMoreObserver.disconnect();
            this.loadMoreObserver = null;
        }

        // Удаляем кнопку "Вверх" и scroll listener
        if (this.boundHandlers.scroll) {
            window.removeEventListener('scroll', this.boundHandlers.scroll);
        }
        if (this.scrollTopButton) {
            this.scrollTopButton.remove();
            this.scrollTopButton = null;
        }

        window.removeEventListener('popstate', this.boundHandlers.popstate);

        if (this.elements.paginationContainer) {
            this.elements.paginationContainer.removeEventListener('click', this.boundHandlers.paginationClick);
        }

        if (this.elements.searchButton) {
            this.elements.searchButton.removeEventListener('click', this.boundHandlers.searchSubmit);
        }

        // Cleanup "Load More" button listener
        if (this.boundHandlers.loadMore) {
            const loadMoreBtn = document.querySelector('.results__more-button');
            if (loadMoreBtn) {
                loadMoreBtn.removeEventListener('click', this.boundHandlers.loadMore);
            }
        }
    }
}
export default SearchPagination;